import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User,
  Trophy,
  Filter,
  Search,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Star,
  Users,
  Award,
  Target,
  Activity,
  Calendar,
  MapPin,
  Hash,
  TrendingUp,
  Crown,
  Medal,
  ArrowRight
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { playersApi } from '../../services/api';
import { useSite } from '../../contexts/SiteContext';
import { useSeason } from '../../contexts/SeasonContext';
import SeasonSelector from '../Common/SeasonSelector';
import { LoadingSpinner, EmptyStates, PlayerCardSkeleton } from '../Common';

// Types for players
interface Player {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  jersey_number?: number;
  position: string;
  team: {
    name: string;
    city: string;
    abbreviation: string;
    sport_name: string;
  };
  height?: string;
  weight?: string;
  age?: number;
  birth_date?: string;
  birth_place?: string;
  years_pro?: number;
  college?: string;
  draft_year?: number;
  draft_round?: number;
  draft_pick?: number;
  career_stats?: {
    games_played?: number;
    points?: number;
    goals?: number;
    assists?: number;
    rebounds?: number;
    tackles?: number;
    yards?: number;
    touchdowns?: number;
    batting_average?: number;
    home_runs?: number;
    rbis?: number;
    era?: number;
    wins?: number;
    saves?: number;
  };
  awards?: string[];
  championships?: number;
  all_star_selections?: number;
  status: 'active' | 'inactive' | 'retired' | 'injured';
  contract_year?: number;
}

const PlayersPage: React.FC = () => {
  const site = useSite();
  const { currentSeason } = useSeason();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);

  // Fetch players from API
  const { data: playersData, isLoading, error } = useQuery({
    queryKey: ['players', site.sport, selectedPosition, selectedStatus, currentSeason],
    queryFn: async () => {
      let response;
      const filters = { season: currentSeason };
      if (selectedStatus === 'active') {
        response = await playersApi.getActive(filters);
      } else if (selectedPosition && selectedPosition !== 'all') {
        response = await playersApi.getByPosition(selectedPosition, filters);
      } else {
        response = await playersApi.getAll(filters);
      }
      console.log('Players API response:', response);
      return response.data || [];
    },
  });

  // Transform API data to match component interface
  console.log('Players API data:', playersData);
  console.log('Players API data length:', playersData?.length);
  const players = {
    data: playersData?.map((player: any) => ({
      id: player.id || Math.random().toString(),
      slug: player.slug || player.person?.slug || player.id, // Add slug field
      first_name: player.person?.first_name || player.first_name || 'Unknown',
      last_name: player.person?.last_name || player.last_name || 'Player',
      full_name: player.person?.full_name || player.full_name || `${player.person?.first_name || ''} ${player.person?.last_name || ''}`.trim() || 'Unknown Player',
      jersey_number: player.jersey_number,
      position: player.primary_position_name || player.primary_position?.name || player.position || 'Player',
      team: {
        name: player.current_team?.name || 'Free Agent',
        city: player.current_team?.city || '',
        abbreviation: player.current_team?.abbreviation || 'FA',
        sport_name: player.current_team?.sport_name || site.sport || 'Sports'
      },
      height: player.height,
      weight: player.weight,
      age: player.person?.age || player.age,
      birth_date: player.person?.birth_date || player.birth_date,
      birth_place: player.person?.birth_place || player.birth_place,
      years_pro: player.years_pro || 0,
      college: player.college,
      draft_year: player.draft_year,
      draft_round: player.draft_round,
      draft_pick: player.draft_pick,
      career_stats: player.career_stats || {},
      awards: player.awards || [],
      championships: player.championships_won || 0,
      all_star_selections: player.all_star_selections || 0,
      status: player.is_active ? 'active' : 'inactive',
      contract_year: player.contract_end_year
    })) || []
  };


  console.log('Players transformed data:', players);
  console.log('Players data array:', players?.data);
  const filteredPlayers = players?.data?.filter((player: any) => {
    const matchesSearch = 
      player.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.team.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.position.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSport = selectedSport === 'all' || player.team.sport_name.toLowerCase() === selectedSport;
    const matchesPosition = selectedPosition === 'all' || player.position.toLowerCase().includes(selectedPosition.toLowerCase());
    const matchesTeam = selectedTeam === 'all' || player.team.abbreviation === selectedTeam;
    const matchesStatus = selectedStatus === 'all' || player.status === selectedStatus;
    return matchesSearch && matchesSport && matchesPosition && matchesTeam && matchesStatus;
  }) || [];
  
  console.log('Filtered players count:', filteredPlayers.length);
  console.log('Filtered players:', filteredPlayers);

  const sports = Array.from(new Set(players?.data?.map(p => p.team?.sport_name).filter(Boolean) || []));
  const positions = Array.from(new Set(players?.data?.map(p => p.position).filter(Boolean) || []));
  const teams = Array.from(new Set(players?.data?.map(p => p.team?.abbreviation).filter(Boolean) || []));

  const activePlayers = filteredPlayers.filter(player => player.status === 'active');
  const retiredPlayers = filteredPlayers.filter(player => player.status === 'retired');
  const injuredPlayers = filteredPlayers.filter(player => player.status === 'injured');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'retired': return 'text-gray-400';
      case 'injured': return 'text-warning';
      case 'inactive': return 'text-error';
      default: return 'text-gray-400';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return 'badge-success';
      case 'retired': return 'badge-secondary';
      case 'injured': return 'badge-warning';
      case 'inactive': return 'badge-error';
      default: return 'badge-secondary';
    }
  };

  const getPlayerIcon = (awards?: string[], championships?: number) => {
    if (championships && championships > 2) return Crown;
    if (awards && awards.some(award => award.includes('MVP'))) return Trophy;
    if (awards && awards.length > 3) return Medal;
    return User;
  };

  const getPlayerColor = (awards?: string[], championships?: number) => {
    if (championships && championships > 2) return 'text-primary';
    if (awards && awards.some(award => award.includes('MVP'))) return 'text-success';
    if (awards && awards.length > 3) return 'text-secondary';
    return 'text-gray-400';
  };

  const formatStatValue = (value?: number, decimal = false) => {
    if (value === undefined) return 'N/A';
    if (decimal) return value.toFixed(3);
    return value.toLocaleString();
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen py-12">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border border-primary/30 mb-6"
            >
              <User className="w-4 h-4 text-primary" />
              <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                Player Directory
              </span>
            </motion.div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="text-white">ALL </span>
              <span className="neon-text">PLAYERS</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Comprehensive player profiles and statistics across all major professional sports
            </p>
          </div>

          {/* Season Selector */}
          <div className="flex justify-center mb-6">
            <SeasonSelector />
          </div>

          {/* Filters */}
          <div className="glass-card p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search players, teams, or positions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-surface/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedSport}
                    onChange={(e) => setSelectedSport(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Sports</option>
                    {sports.map(sport => (
                      <option key={sport} value={sport?.toLowerCase()}>
                        {sport}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative">
                  <Target className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedPosition}
                    onChange={(e) => setSelectedPosition(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Positions</option>
                    {positions.map(position => (
                      <option key={position} value={position}>
                        {position}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Teams</option>
                    {teams.map(team => (
                      <option key={team} value={team}>
                        {team}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative">
                  <Activity className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="injured">Injured</option>
                    <option value="retired">Retired</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 text-center"
            >
              <Activity className="w-8 h-8 text-success mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {activePlayers.length}
              </div>
              <div className="text-sm text-gray-400">Active Players</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 text-center"
            >
              <Trophy className="w-8 h-8 text-primary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {filteredPlayers.reduce((sum, p) => sum + (p.championships || 0), 0)}
              </div>
              <div className="text-sm text-gray-400">Total Championships</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 text-center"
            >
              <Star className="w-8 h-8 text-secondary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {filteredPlayers.reduce((sum, p) => sum + (p.all_star_selections || 0), 0)}
              </div>
              <div className="text-sm text-gray-400">All-Star Selections</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="glass-card p-6 text-center"
            >
              <Users className="w-8 h-8 text-accent mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {sports.length}
              </div>
              <div className="text-sm text-gray-400">Sports Covered</div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Players Grid */}
      <div className="max-w-7xl mx-auto">
        {isLoading ? (
          <PlayerCardSkeleton count={12} />
        ) : error ? (
          <EmptyStates.LoadingError
            error={(error as Error).message || 'Failed to load players data'}
            onRetry={() => window.location.reload()}
          />
        ) : filteredPlayers.length === 0 ? (
          <EmptyStates.NoPlayers />
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredPlayers.map((player) => {
              const PlayerIcon = getPlayerIcon(player.awards, player.championships);
              return (
                <motion.div
                  key={player.id}
                  variants={item}
                  whileHover={{ scale: 1.02 }}
                  className="group"
                >
                  <div 
                    className="glass-card h-full overflow-hidden border border-border hover:border-primary/50 transition-all duration-300 cursor-pointer group-hover:shadow-[0_0_30px_rgba(0,255,136,0.15)]"
                  >
                    {/* Player Header */}
                    <Link to={`/players/${player.slug || player.id}`} className="block p-6 hover:bg-white/5 transition-colors">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="relative">
                            <div className="absolute inset-0 bg-primary/20 blur-xl" />
                            <div className="relative w-16 h-16 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/30 flex items-center justify-center">
                              {player.jersey_number ? (
                                <span className="text-2xl font-black text-primary">
                                  {player.jersey_number}
                                </span>
                              ) : (
                                <PlayerIcon className="w-8 h-8 text-primary" />
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="badge badge-primary text-xs">
                                {player.team.sport_name}
                              </span>
                              <span className={`badge ${getStatusBadge(player.status)} text-xs`}>
                                {player.status.charAt(0).toUpperCase() + player.status.slice(1)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-400">
                              {player.position}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                      </div>

                      {/* Player Info */}
                      <h3 className="text-xl font-bold text-white mb-1 group-hover:text-primary transition-colors">
                        {player.full_name}
                      </h3>
                      
                      <div className="flex items-center space-x-2 mb-4">
                        <div className="w-8 h-8 rounded bg-gradient-to-br from-gray-700 to-gray-800 border border-border flex items-center justify-center">
                          <span className="text-xs font-bold text-white">
                            {player.team.abbreviation}
                          </span>
                        </div>
                        <span className="text-sm text-gray-400">
                          {player.team.name}
                        </span>
                      </div>

                      {/* Key Stats/Info */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                            Age
                          </div>
                          <div className="text-sm font-semibold text-white">
                            {player.age || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                            Years Pro
                          </div>
                          <div className="text-sm font-semibold text-white">
                            {player.years_pro || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                            Championships
                          </div>
                          <div className="text-sm font-semibold text-primary">
                            {player.championships || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                            All-Stars
                          </div>
                          <div className="text-sm font-semibold text-secondary">
                            {player.all_star_selections || 0}
                          </div>
                        </div>
                      </div>

                      {/* Awards Preview */}
                      {player.awards && player.awards.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <Award className="w-4 h-4 text-primary" />
                            <span className="text-xs text-primary font-semibold">
                              Recent Awards
                            </span>
                          </div>
                          <div className="text-xs text-gray-400">
                            {player.awards.slice(0, 2).join(', ')}
                            {player.awards.length > 2 && '...'}
                          </div>
                        </div>
                      )}
                    </Link>

                    {/* Expand Button */}
                    <div className="px-6 pb-4">
                      <button 
                        onClick={() => setExpandedPlayer(
                          expandedPlayer === player.id ? null : player.id
                        )}
                        className="w-full py-2 text-xs text-gray-400 hover:text-primary transition-colors flex items-center justify-center gap-1"
                      >
                        {expandedPlayer === player.id ? (
                          <>
                            <ChevronUp className="w-4 h-4" />
                            Show Less
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" />
                            Show More Stats
                          </>
                        )}
                      </button>
                    </div>

                    {/* Expanded Details */}
                    <AnimatePresence>
                      {expandedPlayer === player.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="border-t border-border/50 bg-surface/20"
                        >
                          <div className="p-6 space-y-6">
                            {/* Physical Stats */}
                            <div>
                              <h4 className="text-sm font-bold text-primary mb-3 uppercase tracking-wider">
                                Physical
                              </h4>
                              <div className="grid grid-cols-2 gap-4">
                                {player.height && (
                                  <div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                      Height
                                    </div>
                                    <div className="text-white font-semibold">
                                      {player.height}
                                    </div>
                                  </div>
                                )}
                                {player.weight && (
                                  <div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                      Weight
                                    </div>
                                    <div className="text-white font-semibold">
                                      {player.weight}
                                    </div>
                                  </div>
                                )}
                                {player.birth_place && (
                                  <div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                      Birthplace
                                    </div>
                                    <div className="text-white font-semibold">
                                      {player.birth_place}
                                    </div>
                                  </div>
                                )}
                                {player.college && (
                                  <div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                      College
                                    </div>
                                    <div className="text-white font-semibold">
                                      {player.college}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Draft Info */}
                            {(player.draft_year || player.draft_round) && (
                              <div>
                                <h4 className="text-sm font-bold text-primary mb-3 uppercase tracking-wider">
                                  Draft
                                </h4>
                                <div className="grid grid-cols-3 gap-4">
                                  {player.draft_year && (
                                    <div>
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                        Year
                                      </div>
                                      <div className="text-white font-semibold">
                                        {player.draft_year}
                                      </div>
                                    </div>
                                  )}
                                  {player.draft_round && (
                                    <div>
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                        Round
                                      </div>
                                      <div className="text-white font-semibold">
                                        {player.draft_round}
                                      </div>
                                    </div>
                                  )}
                                  {player.draft_pick && (
                                    <div>
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                        Pick
                                      </div>
                                      <div className="text-white font-semibold">
                                        {player.draft_pick}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Career Stats */}
                            {player.career_stats && (
                              <div>
                                <h4 className="text-sm font-bold text-primary mb-3 uppercase tracking-wider">
                                  Career Stats
                                </h4>
                                <div className="grid grid-cols-2 gap-4">
                                  {Object.entries(player.career_stats)
                                    .filter(([_, value]) => value !== undefined)
                                    .slice(0, 6)
                                    .map(([key, value]) => (
                                      <div key={key}>
                                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                          {key.replace(/_/g, ' ')}
                                        </div>
                                        <div className="text-white font-semibold">
                                          {key.includes('average') || key.includes('era') 
                                            ? formatStatValue(value as number, true) 
                                            : formatStatValue(value as number)}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}

                            {/* All Awards */}
                            {player.awards && player.awards.length > 0 && (
                              <div>
                                <h4 className="text-sm font-bold text-primary mb-3 uppercase tracking-wider">
                                  Awards & Honors
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {player.awards.map((award: any, index: number) => (
                                    <span key={index} className="badge badge-secondary text-xs">
                                      {award}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PlayersPage;